import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, X, Check, ChefHat, Loader2, Award, Download, ArrowRight, RotateCcw, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { printCertificate } from "@/lib/certificatePdf";
import CertifiedBadge from "@/components/CertifiedBadge";

// The AI Concierge: floating chat bubble bottom-right that walks a new chef
// through the "Certified Culinary Specialist" program: 10-question Precision
// Quiz + Recipe-to-Inventory activation gate + final certificate.
//
// Persists dismissed state locally so users aren't nagged; badge holders still
// see the bubble (they can re-open to reprint the certificate).

const DISMISSED_KEY = "cs_concierge_dismissed_v1";

function useCertStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/certification/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);
  return { status, loading, refresh };
}

export default function Concierge() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [bubbleHidden, setBubbleHidden] = useState(false);
  const { status, loading, refresh } = useCertStatus();

  useEffect(() => {
    try {
      setBubbleHidden(localStorage.getItem(DISMISSED_KEY) === "1");
    } catch {}
  }, []);

  // Certified chefs get to keep the bubble on hand (small, non-intrusive): it
  // lets them reprint the certificate whenever they want.
  const showBubble = !!user && !bubbleHidden;

  if (!showBubble) return null;

  const dot = status && !status.certified;
  const overlay = (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open Kitchen Concierge"
        data-testid="concierge-bubble"
        className="fixed z-[900] bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center group"
        style={{
          background: "linear-gradient(160deg, #1a1509 0%, #0b0b0d 100%)",
          border: "1px solid rgba(232,207,154,0.5)",
          boxShadow: "0 12px 30px -8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 0 4px rgba(232,207,154,0.05)",
        }}
      >
        <ChefHat className="w-5 h-5 transition-transform group-hover:scale-110" style={{ color: "#f2dfb5" }} strokeWidth={1.5} />
        {status?.certified ? (
          <span
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
            style={{ background: "#c8a96a", color: "#0b0b0d" }}
            aria-label="Certified"
          >
            <Check className="w-2.5 h-2.5" strokeWidth={3} />
          </span>
        ) : dot ? (
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: "#f2dfb5", boxShadow: "0 0 8px rgba(232,207,154,0.9)" }} />
        ) : null}
      </button>

      {/* Slide-in panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="cx-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[950] bg-black/50"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              key="cx-panel"
              initial={{ opacity: 0, x: 40, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: 40, y: 10 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="fixed z-[951] bottom-4 right-4 top-4 w-full max-w-[420px] rounded-[24px] p-0 flex flex-col overflow-hidden"
              style={{
                background: "linear-gradient(180deg, rgba(20,17,12,0.96), rgba(11,11,13,0.98))",
                border: "1px solid rgba(232,207,154,0.30)",
                boxShadow: "0 40px 100px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)",
                backdropFilter: "blur(20px) saturate(160%)",
                WebkitBackdropFilter: "blur(20px) saturate(160%)",
              }}
              data-testid="concierge-panel"
            >
              <ConciergeContent
                status={status}
                loading={loading}
                refresh={refresh}
                user={user}
                onClose={() => setOpen(false)}
                onDismissForever={() => {
                  try { localStorage.setItem(DISMISSED_KEY, "1"); } catch {}
                  setBubbleHidden(true);
                  setOpen(false);
                }}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );

  return createPortal(overlay, document.body);
}

function ConciergeContent({ status, loading, refresh, user, onClose, onDismissForever }) {
  const [mode, setMode] = useState("home"); // home | quiz | quizResult | issued
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [finalizing, setFinalizing] = useState(false);

  // Auto-jump straight to the celebratory view when already certified.
  useEffect(() => {
    if (status?.certified) setMode("issued");
  }, [status?.certified]);

  const questions = status?.questions || [];
  const canSubmitQuiz = questions.length > 0 && questions.every((q) => quizAnswers[q.id] != null);

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/certification/quiz", {
        answers: questions.map((q) => ({ id: q.id, choice: quizAnswers[q.id] })),
      });
      setQuizResult(data);
      setMode("quizResult");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const retryQuiz = () => {
    setQuizAnswers({});
    setQuizResult(null);
    setMode("quiz");
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      const { data } = await api.post("/certification/finalize");
      toast.success(`Certified: ${data.certificate_number}`);
      await refresh();
      setMode("issued");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setFinalizing(false);
    }
  };

  const openCert = () => {
    printCertificate({
      name: user?.name,
      email: user?.email,
      certificateNumber: status?.certificate_number,
      certifiedAt: status?.certified_at,
    });
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(135deg, rgba(232,207,154,0.35), rgba(200,169,106,0.08))",
            border: "1px solid rgba(232,207,154,0.4)",
          }}
        >
          <ChefHat className="w-4 h-4" strokeWidth={1.5} style={{ color: "#f2dfb5" }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-white/95 leading-tight">Kitchen Concierge</div>
          <div className="text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: "rgba(232,207,154,0.85)" }}>
            Certification Programme
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white/90 hover:bg-white/5"
          aria-label="Close"
          data-testid="concierge-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {loading || !status ? (
          <div className="flex items-center justify-center h-40 text-white/50 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your progress…
          </div>
        ) : mode === "home" ? (
          <HomeView status={status} onStartQuiz={() => setMode("quiz")} onFinalize={finalize} finalizing={finalizing} />
        ) : mode === "quiz" ? (
          <QuizView
            questions={questions}
            answers={quizAnswers}
            onPick={(qid, idx) => setQuizAnswers((a) => ({ ...a, [qid]: idx }))}
            onSubmit={submitQuiz}
            canSubmit={canSubmitQuiz}
            submitting={submitting}
            onBack={() => setMode("home")}
          />
        ) : mode === "quizResult" ? (
          <QuizResultView
            result={quizResult}
            questions={questions}
            answers={quizAnswers}
            onRetry={retryQuiz}
            onContinue={async () => {
              await refresh();
              setMode("home");
            }}
          />
        ) : mode === "issued" ? (
          <IssuedView status={status} onPrint={openCert} onDismissForever={onDismissForever} />
        ) : null}
      </div>
    </>
  );
}

function ProgressStep({ done, label, hint }) {
  return (
    <div className="flex items-start gap-3 py-2">
      {done ? (
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#c8a96a" }} />
      ) : (
        <Circle className="w-4 h-4 mt-0.5 shrink-0 text-white/25" />
      )}
      <div className="min-w-0">
        <div className={`text-sm ${done ? "text-white/85" : "text-white/60"}`}>{label}</div>
        {hint && !done && <div className="text-[0.7rem] text-white/40 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function HomeView({ status, onStartQuiz, onFinalize, finalizing }) {
  const act = status.activation || {};
  const quizPassed = !!status.quiz_passed_at;
  const eligible = status.eligible_to_certify;

  return (
    <>
      <p className="text-sm text-white/70 leading-relaxed">
        Welcome. I'll help you earn the <span className="text-white/95">Certified Culinary Specialist</span> badge. It's a mark that your kitchen data is precise and your menu math is honest.
      </p>
      <div className="rounded-2xl p-4" style={{ background: "rgba(232,207,154,0.05)", border: "1px solid rgba(232,207,154,0.18)" }}>
        <div className="label-eyebrow text-[0.55rem] mb-2" style={{ color: "rgba(232,207,154,0.85)" }}>
          Two gates
        </div>
        <div>
          <ProgressStep
            done={quizPassed}
            label="Pass the 10-question Precision Quiz"
            hint="90% pass bar: covers yield math, food-cost %, deductions, digest, snapshots and Recipe→Inventory semantics."
          />
          <div className="pl-7 -mt-1 mb-1">
            <button
              onClick={onStartQuiz}
              disabled={quizPassed}
              className="gold-cta h-8 px-4 rounded-lg inline-flex items-center gap-1 text-xs font-semibold disabled:opacity-50 disabled:cursor-default"
              data-testid="concierge-start-quiz"
            >
              {quizPassed ? "Quiz passed" : (<>Start quiz <ArrowRight className="w-3.5 h-3.5" /></>)}
            </button>
          </div>
        </div>
        <div className="mt-2">
          <div className="text-sm text-white/85 mb-1.5">Complete a Recipe-to-Inventory link</div>
          <div className="pl-1 space-y-0.5">
            <ProgressStep done={act.ingredient_added} label="Add an ingredient" hint="Ingredients → New" />
            <ProgressStep done={act.recipe_built} label="Build a recipe with ≥1 ingredient" hint="Recipes → New → Add ingredient row" />
            <ProgressStep done={act.sale_logged} label="Log a sale on that recipe" hint="Any recipe → Sell" />
          </div>
        </div>
      </div>

      {eligible && (
        <button
          onClick={onFinalize}
          disabled={finalizing}
          className="gold-cta w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold"
          data-testid="concierge-finalize"
        >
          {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
          Award my certificate
        </button>
      )}
      {!eligible && (
        <p className="text-[0.7rem] text-white/45 leading-relaxed">
          Both gates need a check-mark before I can print your name on the certificate.
        </p>
      )}
    </>
  );
}

function QuizView({ questions, answers, onPick, onSubmit, canSubmit, submitting, onBack }) {
  return (
    <>
      <div className="flex items-center gap-2">
        <Sparkles className="w-3 h-3" style={{ color: "#f2dfb5" }} />
        <div className="label-eyebrow text-[0.55rem]" style={{ color: "rgba(232,207,154,0.85)" }}>
          Precision Quiz • 90% to pass
        </div>
      </div>
      {questions.map((q, i) => (
        <div key={q.id} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }} data-testid={`quiz-q-${q.id}`}>
          <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/40 mb-1.5">Question {i + 1}</div>
          <div className="text-sm text-white/90 leading-relaxed mb-3">{q.prompt}</div>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => {
              const active = answers[q.id] === oi;
              return (
                <button
                  key={oi}
                  onClick={() => onPick(q.id, oi)}
                  className={`w-full text-left rounded-lg px-3 py-2 text-xs leading-relaxed transition-colors`}
                  style={{
                    background: active ? "rgba(232,207,154,0.14)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${active ? "rgba(232,207,154,0.55)" : "rgba(255,255,255,0.08)"}`,
                    color: active ? "#f2dfb5" : "rgba(255,255,255,0.78)",
                  }}
                  data-testid={`quiz-${q.id}-opt-${oi}`}
                >
                  <span className="inline-block w-4 mr-2 font-mono opacity-70">{String.fromCharCode(65 + oi)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <button onClick={onBack} className="glass-ghost h-10 px-4 rounded-lg text-xs font-medium text-white/85" data-testid="concierge-quiz-back">Back</button>
        <button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className="gold-cta flex-1 h-10 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50"
          data-testid="concierge-quiz-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit answers"}
        </button>
      </div>
    </>
  );
}

function QuizResultView({ result, questions, answers, onRetry, onContinue }) {
  const byId = useMemo(() => Object.fromEntries(questions.map((q) => [q.id, q])), [questions]);
  return (
    <>
      <div
        className="rounded-2xl p-4 text-center"
        style={{
          background: result.passed
            ? "linear-gradient(180deg, rgba(120,180,120,0.14), rgba(120,180,120,0.03))"
            : "linear-gradient(180deg, rgba(220,120,90,0.14), rgba(220,120,90,0.03))",
          border: `1px solid ${result.passed ? "rgba(120,180,120,0.35)" : "rgba(220,120,90,0.35)"}`,
        }}
        data-testid="concierge-quiz-result"
      >
        <div className="font-display text-2xl mb-1" style={{ color: result.passed ? "#a8d8a8" : "#e8a890" }}>
          {result.passed
            ? `${result.score} / ${result.total} • Passed`
            : `${result.score} / ${result.total} • Not yet (need ${result.pass_threshold || Math.ceil(result.total * 0.9)})`}
        </div>
        <div className="text-xs text-white/60">
          {result.passed
            ? "Precision confirmed. Coach's feedback below: read it, it's the good stuff."
            : `Coaching feedback below. 90% is the pass bar: read every explanation, then try again.`}
        </div>
      </div>

      {result.results.map((r, i) => {
        const q = byId[r.id];
        const chosen = answers[r.id];
        return (
          <div key={r.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-2 mb-1.5">
              {r.correct
                ? <Check className="w-3.5 h-3.5" style={{ color: "#a8d8a8" }} />
                : <X className="w-3.5 h-3.5" style={{ color: "#e8a890" }} />}
              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/40">Question {i + 1}</div>
            </div>
            <div className="text-xs text-white/80 mb-2">{q?.prompt}</div>
            <div className="text-xs mb-1">
              <span className="text-white/50">Your answer: </span>
              <span className={r.correct ? "text-white/90" : "text-white/60 line-through"}>{q?.options?.[chosen]}</span>
            </div>
            {!r.correct && (
              <div className="text-xs mb-2">
                <span className="text-white/50">Correct: </span>
                <span style={{ color: "#f2dfb5" }}>{q?.options?.[r.correct_index]}</span>
              </div>
            )}
            <div className="text-[0.7rem] leading-relaxed text-white/60 pt-2 border-t border-white/5">{r.explanation}</div>
          </div>
        );
      })}

      <div className="flex items-center gap-2 pt-1">
        {result.passed ? (
          <button onClick={onContinue} className="gold-cta w-full h-10 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold" data-testid="concierge-quiz-continue">
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={onRetry} className="gold-cta w-full h-10 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold" data-testid="concierge-quiz-retry">
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        )}
      </div>
    </>
  );
}

function IssuedView({ status, onPrint, onDismissForever }) {
  return (
    <div className="text-center space-y-4 py-4">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full"
        style={{
          background: "radial-gradient(circle at 30% 30%, rgba(232,207,154,0.35), rgba(200,169,106,0.05))",
          border: "1.5px solid rgba(232,207,154,0.7)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 8px 24px rgba(200,169,106,0.28)",
        }}
      >
        <Award className="w-9 h-9" style={{ color: "#f2dfb5" }} strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-display text-2xl text-white/95">You're certified.</div>
        <div className="mt-1 text-xs text-white/60">Congratulations, Chef.</div>
      </div>
      <div className="flex items-center justify-center">
        <CertifiedBadge size="lg" certificateNumber={status.certificate_number} />
      </div>
      <p className="text-xs text-white/60 leading-relaxed px-2">
        Your badge is now visible on your dashboard sidebar, your billing page and any public journal profile you create. Print the certificate and put it up where your team can see it.
      </p>
      <div className="space-y-2 pt-2">
        <button
          onClick={onPrint}
          className="gold-cta w-full h-10 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold"
          data-testid="concierge-print-cert"
        >
          <Download className="w-4 h-4" /> Print certificate
        </button>
        <button
          onClick={onDismissForever}
          className="text-xs text-white/40 hover:text-white/70 transition-colors"
          data-testid="concierge-dismiss"
        >
          Hide the concierge bubble
        </button>
      </div>
    </div>
  );
}
